<script setup lang="ts">
import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { PerfectScrollbar } from 'vue3-perfect-scrollbar'

import GameLog from '@/components/GameLog.vue'
import UiPanelWrapper from '@/components/shared/UiPanelWrapper.vue'
import UiPanel from '@/components/shared/UiPanel.vue'
import UiSwitcher from '@/components/shared/UiSwitcher.vue'

import { useUiState } from '@/stores/uiState'
import { useGameState } from '@/stores/gameState'

const { roomState } = storeToRefs(useGameState())
const { getPlayerById, getSeatForPlayer } = useGameState()
const {
  panels: { sessionLog }
} = useUiState()

const tabs = [
  { key: 'ledger', value: 'ledger', text: 'Ledger' },
  { key: 'log', value: 'log', text: 'Game Log' }
]
const selectedTab = ref('ledger')
const formatter = new Intl.NumberFormat('en-US', {
  style: 'decimal',
  maximumFractionDigits: 2
})

const getStack = (playerId: string) => {
  const seat = getSeatForPlayer(playerId)
  if (!seat) {
    return 0
  }

  return seat.stack
}

const getNet = (playerId: string) => {
  const seat = getSeatForPlayer(playerId)
  if (!seat) {
    return 0
  }

  const amount =
    seat.stack -
    (roomState.value.ledger[playerId].buyIn ?? 0) +
    (roomState.value.ledger[playerId].buyOut ?? 0)

  const sign = amount === 0 ? '' : amount > 0 ? '+' : '-'

  return `${sign}${formatter.format(Math.abs(amount))}`
}
</script>
<template>
  <ui-panel-wrapper drawer :open="sessionLog.isOpen" @close="sessionLog.close()">
    <ui-panel
      class="session-log z-9999 p-4!"
      :title="$t('Compete.label.SessionLog')"
      :show-back-label="false"
      @close="sessionLog.close()"
    >
      <ui-switcher v-model="selectedTab" :items="tabs"></ui-switcher>
      <perfect-scrollbar v-if="selectedTab === 'ledger'" class="session-log__table" tag="table">
        <thead>
          <tr>
            <th class="session-log__table-header">Player</th>
            <!-- <th class="session-log__table-header">Buy-in</th> -->
            <!-- <th class="session-log__table-header">Buy-out</th> -->
            <th class="session-log__table-header">Stack</th>
            <th class="session-log__table-header">Net</th>
          </tr>
        </thead>
        <tbody>
          <tr
            class="session-log__table-row"
            v-for="(row, playerId) in roomState.ledger"
            :key="playerId"
          >
            <td class="session-log__table-cell">
              {{ row.name ?? 'Unknown' }}
            </td>
            <!-- <td class="session-log__table-cell">{{ formatter.format(row.buyIn) }}</td> -->
            <!-- <td class="session-log__table-cell">{{ formatter.format(row.buyOut) }}</td> -->
            <td class="session-log__table-cell">
              {{ formatter.format(row.stack) }}
            </td>
            <td class="session-log__table-cell">
              {{ row.net > 0 ? '+' : '' }}{{ formatter.format(row.net) }}
            </td>
          </tr>
        </tbody>
      </perfect-scrollbar>
      <game-log v-if="selectedTab === 'log'" />
    </ui-panel>
  </ui-panel-wrapper>
</template>

<style lang="scss">
.session-log {
  position: absolute;
  top: 70px;
  bottom: 110px;
  left: 24px;
  z-index: 9999;

  &__table {
    width: calc(100% + 32px);
    margin-left: -16px;
    margin-right: -16px;
    table-layout: fixed;
    overflow: hidden;
    border-collapse: collapse;
  }

  &__table-header {
    color: #86899e;
    text-align: left;
    font-size: 14px;
    font-weight: 500;
    padding: 12px 16px;
    white-space: nowrap;
    vertical-align: middle;
  }

  &__table-row {
    &:nth-child(even) {
      background: #262736;
    }
  }

  &__table-cell {
    color: #fff;
    font-size: 14px;
    font-weight: 400;
    padding: 12px 16px;
    vertical-align: middle;
  }
}
</style>
